import {
  Color3,
  CreateGroundFromHeightMapVertexData,
  RawTexture,
  Texture,
} from "@babylonjs/core";
import { GroundMesh } from "@babylonjs/core/Meshes/groundMesh";
import { Scene } from "@babylonjs/core/scene";
import { MixMaterial } from "@babylonjs/materials";
import { TextureType } from "./generateV1";

export function getPixel(
  imageData: ImageData,
  x: number,
  y: number,
  width: number
) {
  const red = (y * (width * 4) + x * 4) % imageData.data.length;

  return [
    imageData.data[red],
    imageData.data[red + 1],
    imageData.data[red + 2],
  ];
}

export function getPixelFromArray(
  imageData: Array<number>,
  x: number,
  y: number,
  width: number
) {
  const red = (y * (width * 4) + x * 4) % imageData.length;

  return [imageData[red], imageData[red + 1], imageData[red + 2]];
}

export const drawPixel = (
  imageData: ImageData,
  x: number,
  y: number,
  width: number,
  pixel: number[]
) => {
  const red = (y * (width * 4) + x * 4) % imageData.data.length;

  for (let k = 0; k < 3; k++) {
    imageData.data[red + k] = pixel[k];
  }

  imageData.data[red + 3] = pixel[3] !== undefined ? pixel[3] : 255;
};

export const drawPixelInArray = (
  imageData: Array<number>,
  x: number,
  y: number,
  width: number,
  pixel: number[]
) => {
  const red = (y * (width * 4) + x * 4) % imageData.length;

  for (let k = 0; k < 3; k++) {
    imageData[red + k] = pixel[k];
  }

  imageData[red + 3] = pixel[3] !== undefined ? pixel[3] : 255;
};

export const getImagePixels = (image: ImageBitmap) => {
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = image.width;
  tempCanvas.height = image.height;

  const ctx = tempCanvas.getContext("2d");
  ctx?.drawImage(image, 0, 0);

  const imageData = ctx?.getImageData(
    0,
    0,
    tempCanvas.width,
    tempCanvas.height
  );

  return imageData;
};

function lerp(a: number, b: number, amount: number) {
  return (1 - amount) * a + amount * b;
}

function exponentialLerp(a: number, b: number, amount: number) {
  let x = Math.exp(-0.5 + amount * 2) - 1;
  return lerp(a, b, x);
}

export const getColorByHeight2 = (
  height: number,
  levels: [number, number, number]
) => {
  if (height <= levels[0]) {
    return [255, exponentialLerp(0, 127, height / levels[0]), 0, 255];
  }
  if (height <= levels[1]) {
    return [
      0,
      255,
      exponentialLerp(0, 127, (height - levels[0]) / (levels[1] - levels[0])),
      255,
    ];
  }
  if (height <= levels[2]) {
    return [
      0,
      0,
      255,
      255 -
        exponentialLerp(0, 127, (height - levels[1]) / (levels[2] - levels[1])),
    ];
  }

  return [0, 0, 0, 0];
};

/**
 * @deprecated
 */
export const getColorByHeight = (
  height: number,
  levels: [number, number, number]
) => {
  if (height <= levels[0]) {
    return [255, 0, 0, 255];
  }
  if (height <= levels[1]) {
    return [0, 255, 0, 255];
  }
  if (height <= levels[2]) {
    return [0, 0, 255, 255];
  }

  return [0, 0, 0, 0];
};

export const getImageBinary = (
  canvas: HTMLCanvasElement | null | undefined,
  resolution: { x: number; y: number }
) => {
  if (!canvas) {
    return null;
  }
  const ctx = canvas
    .getContext("2d", { willReadFrequently: true })
    ?.getImageData(0, 0, resolution.x, resolution.y);

  if (!ctx) {
    return null;
  }
  const data = ctx.data;
  const buffer = new ArrayBuffer(data.length);
  const binary = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) {
    binary[i] = data[i];
  }

  return binary;
};

export const getSpotlightAngle = (radius: number, height: number) => {
  return Math.atan(radius / height) / 2;
};

export const updateTerrain = (
  ground: GroundMesh | undefined,
  canvas: HTMLCanvasElement | null | undefined,
  resolution: { x: number; y: number },
  heightScale: number,
  mapSize: number,
  subdivisions: number
) => {
  const binary = getImageBinary(canvas, resolution);

  if (!ground || !binary) {
    return;
  }

  const bufferWidth = resolution.x;
  const bufferHeight = resolution.y;

  const filter = new Color3(0.3, 0.59, 0.11);

  const vertexData = CreateGroundFromHeightMapVertexData({
    width: mapSize,
    height: mapSize,
    subdivisions: subdivisions,
    maxHeight: heightScale / 2 || 1,
    minHeight: -heightScale / 2 || -1,
    colorFilter: filter,
    buffer: binary,
    bufferWidth: bufferWidth,
    bufferHeight: bufferHeight,
    alphaFilter: 0.0,
  });

  vertexData.applyToMesh(ground, true);

  ground._setReady(true);
};

export const updateTerrainTexture = (
  gameScene: Scene | undefined | null,
  resolution: { x: number; y: number },
  canvas: HTMLCanvasElement | null | undefined,
  textures: [TextureType, TextureType, TextureType, TextureType]
) => {
  if (!gameScene) {
    return;
  }

  const currentMaterial = gameScene.getMaterialByName(
    "terrainMaterial"
  ) as MixMaterial;
  if (currentMaterial) {
    currentMaterial.mixTexture1.dispose();
    const rawTexture = RawTexture.CreateRGBATexture(
      getImageBinary(canvas, resolution),
      resolution.x,
      resolution.y,
      gameScene,
      undefined,
      true
    );
    currentMaterial.mixTexture1 = rawTexture;

    const newTextures = textures.map(
      (texture) =>
        (gameScene.getTextureByName(
          `textures/${texture.name}.jpg`
        ) as Texture) || new Texture(`textures/${texture.name}.jpg`, gameScene)
    );

    currentMaterial.diffuseTexture1 = newTextures[0];
    currentMaterial.diffuseTexture2 = newTextures[1];
    currentMaterial.diffuseTexture3 = newTextures[2];
    currentMaterial.diffuseTexture4 = newTextures[3];

    currentMaterial.diffuseTexture1.uScale =
      currentMaterial.diffuseTexture1.vScale = textures[0].uv || 50;

    currentMaterial.diffuseTexture2.uScale =
      currentMaterial.diffuseTexture2.vScale = textures[1].uv || 50;

    currentMaterial.diffuseTexture3.uScale =
      currentMaterial.diffuseTexture3.vScale = textures[2].uv || 50;

    currentMaterial.diffuseTexture4.uScale =
      currentMaterial.diffuseTexture4.vScale = textures[3].uv || 50;
  }
};

export function rgbToHex(r: number, g: number, b: number): string {
  let rHex = Math.round(r * 255).toString(16);
  let gHex = Math.round(g * 255).toString(16);
  let bHex = Math.round(b * 255).toString(16);

  if (rHex.length == 1) rHex = "0" + rHex;
  if (gHex.length == 1) gHex = "0" + gHex;
  if (bHex.length == 1) bHex = "0" + bHex;

  return "#" + rHex + gHex + bHex;
}

export function hexToRgb(hex: string): number[] {
  let r = 0,
    g = 0,
    b = 0;

  // Handling 3 digit hex
  if (hex.length == 4) {
    r = parseInt(hex[1] + hex[1], 16) / 255;
    g = parseInt(hex[2] + hex[2], 16) / 255;
    b = parseInt(hex[3] + hex[3], 16) / 255;
  }
  // Handling 6 digit hex
  else if (hex.length == 7) {
    r = parseInt(hex[1] + hex[2], 16) / 255;
    g = parseInt(hex[3] + hex[4], 16) / 255;
    b = parseInt(hex[5] + hex[6], 16) / 255;
  }

  return [r, g, b];
}
